import React from "react";
import Autocomplete from "@mui/material/Autocomplete";

import { useFormik } from "formik";
import YupObject from "yup/lib/object";
import YupString from "yup/lib/string";
import { StatusENUM } from "types/common";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import FormHelperText from "@mui/material/FormHelperText";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import SuccessFailedModal from "components/SuccessFailedModal";
import { Button } from "components/Buttons";
import { Header } from "components/Header/Header";
import StyledDemographicInfo from "./DemographicInfo.style";
import PhoneInput from "components/PhoneInput";

import { ClinicRepository } from "repositories/ClinicRepository";
import { useAppSelector, useAppDispatch } from "hooks/reduxHooks";
import { updateCurrentClinicianAvatar } from "store/ClinicianStore/actions";
import { ButtonColorScheme, ButtonType } from "types/ui";
import { AuthRepository } from "repositories/AuthRepository";
import { setCurrentClinician } from "store/ClinicianStore/clinicianSlice";
import { allowedFileExtensions, allowedImgTypes } from "constants/utils";
import { alphabeticalOnlyRegexWithSpaces } from "constants/regex";
import { capitalize } from "utils/formatters";
const validationSchema = new YupObject().shape({
  city: new YupString().trim().required("City is required"),
  state: new YupString().trim().required("State is required"),
  phoneNumber: new YupString()
    .trim()
    .min(12, "Phone number minumum value has 15 digits")
    .required("Phone number is required"),
  license: new YupString().trim(),
});

const states = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];
const DemographicInfo = () => {
  const [isError, setIsError] = React.useState(false);
  const [uploadAvatarError, setUploadAvatarError] = React.useState("");
  const [isSuccessModalOpen, setIsSuccessModalOpen] = React.useState(false);
  const user = useAppSelector(
    ({ currentClinician }) => currentClinician.currentClinician
  );
  const updateAvatarLoading = useAppSelector(
    ({ currentClinician }) => currentClinician.updateAvatarLoading
  );

  const dispatch = useAppDispatch();
  const hiddenFileInput = React.useRef<HTMLInputElement | null>(null);

  const handleClick = () => {
    if (hiddenFileInput.current) {
      hiddenFileInput.current.click();
    }
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setUploadAvatarError("");
    const fileUploaded = event.target.files?.[0];
    if (!fileUploaded) {
      return;
    }

    if (!allowedImgTypes.includes(fileUploaded.type)) {
      setUploadAvatarError(
        "Please select one of the allowed formats: " +
          allowedFileExtensions.join(", ")
      );
    }
    const formData = new FormData();
    formData.append("fileExtension", fileUploaded.type.split("/")[1]);
    formData.append("contentType", fileUploaded.type);
    formData.append("file", fileUploaded);
    dispatch(
      updateCurrentClinicianAvatar(
        formData,
        () => {},
        () => {
          setUploadAvatarError("Error while uploading image");
        }
      )
    );

    hiddenFileInput.current!.value = "";
  };

  const formik = useFormik({
    initialValues: {
      city: user?.profileInfo.city || "",
      state: user?.profileInfo.state || "",
      phoneNumber: user?.profileInfo.phoneNumber || "",
      license: user?.profileInfo.license || "",
    },
    validationSchema,
    onSubmit: async (data) => {
      try {
        if (!user?._id) {
          return;
        }
        const body = {
          city: data.city,
          state: data.state,
          phoneNumber: data.phoneNumber,
          license: data.license,
          authStatus: StatusENUM.registered,
        };

        const clinicRepository = ClinicRepository.getInstance();
        await clinicRepository.updateCurrentClinician(user?._id, body);
        const authRepository = AuthRepository.getInstance();
        const currentClinician = await authRepository.getMe();
        dispatch(setCurrentClinician(currentClinician));
        setIsSuccessModalOpen(true);
      } catch (err) {
        setIsError(true);
      }
    },
  });

  return (
    <>
      <Header showSettings={false} />
      <StyledDemographicInfo
        container
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <Grid padding={3} className="general_info__form-container">
          <Typography variant="h4" className="general_info__title">
            General Info
          </Typography>

          <Typography className="general_info__subtitle">
            Please provide the info below.
          </Typography>

          <Grid
            container
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            mt={3}
            mb={2}
          >
            <Avatar
              alt="image"
              src={user?.imageUrl}
              sx={{ width: 110, height: 110, marginBottom: 2 }}
            />

            <Button
              onClick={handleClick}
              classNames="general_info__upload-button"
              disabled={updateAvatarLoading}
              style={{
                backgroundColor: updateAvatarLoading ? "lightgray" : undefined,
              }}
            >
              Upload Your Photo
              {updateAvatarLoading && (
                <CircularProgress
                  size="1em"
                  style={{ marginLeft: 10, marginBottom: -3, color: "white" }}
                />
              )}
            </Button>

            <p className="text-red-600 text-xs">{uploadAvatarError}</p>
            <input
              type="file"
              ref={hiddenFileInput}
              onChange={handleChange}
              style={{ display: "none" }}
            />
          </Grid>

          <TextField
            variant="standard"
            placeholder="Clinic"
            value={user?.clinic?.name}
            disabled
            fullWidth
            className="general_info__text-input"
            InputProps={{
              disableUnderline: true,
              style: {
                color: "#5C5D68",
              },
            }}
          />

          <TextField
            variant="standard"
            placeholder="City"
            value={formik.values.city}
            fullWidth
            className="general_info__text-input"
            InputProps={{
              disableUnderline: true,
              style: {
                color: "#5C5D68",
              },
            }}
            onChange={(event) => {
              const value = event.target.value;
              if (alphabeticalOnlyRegexWithSpaces.test(value)) {
                formik.setFieldValue("city", capitalize(value));
              }
            }}
            onBlur={formik.handleBlur}
            name="city"
            sx={{
              border:
                formik.touched.city && formik.errors.city
                  ? "1px solid #DD3341 !important"
                  : "",
            }}
          />
          {formik.touched.city && formik.errors.city && (
            <FormHelperText
              error={Boolean(formik.touched.city && formik.errors.city)}
            >
              {formik.errors.city}
            </FormHelperText>
          )}

          <Autocomplete
            disablePortal
            options={states}
            sx={{
              mb: 1,
              mt: 1,
              borderRadius: "10px",
              zIndex: 1000,
            }}
            value={formik.values.state}
            renderOption={(props, option) => (
              <li {...props} key={option}>
                {option}
              </li>
            )}
            onChange={(ev, newValue) => {
              formik.setFieldValue("state", newValue);
            }}
            renderInput={(params) => (
              <TextField
                label={"State"}
                onBlur={formik.handleBlur}
                placeholder={"State"}
                error={
                  formik.touched.state && formik.errors.state ? true : false
                }
                helperText={
                  formik.touched.state && formik.errors.state
                    ? formik.errors.state
                    : ""
                }
                {...params}
              />
            )}
          />

          {/*     {formik.touched.state && formik.errors.state && (
            <FormHelperText
              error={Boolean(formik.touched.state && formik.errors.state)}
            >
              {formik.errors.state}
            </FormHelperText>
          )} */}

          <TextField
            variant="standard"
            placeholder="License"
            value={formik.values.license}
            fullWidth
            className="general_info__text-input"
            InputProps={{
              disableUnderline: true,
              style: {
                color: "#5C5D68",
              },
            }}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            name="license"
          />
          <PhoneInput
            value={formik.values.phoneNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            name="phoneNumber"
            error={Boolean(
              formik.touched.phoneNumber && formik.errors.phoneNumber
            )}
            helperText={
              formik.touched.phoneNumber ? formik.errors.phoneNumber : ""
            }
            style={{ margin: "6px 0" }}
          />
        </Grid>

        <Grid className="general_info__submit-button-container">
          <Button
            onClick={formik.handleSubmit}
            type={ButtonType.normal}
            colorScheme={
              formik.isSubmitting || updateAvatarLoading
                ? ButtonColorScheme.disabled
                : ButtonColorScheme.gradient
            }
            shadowed
            classNames="general_info__submit-button"
            disabled={formik.isSubmitting || updateAvatarLoading}
          >
            {formik.isSubmitting ? <CircularProgress size={20} /> : "Submit"}
          </Button>
        </Grid>
      </StyledDemographicInfo>

      <SuccessFailedModal
        isError={isError}
        successMessage="Successfully updated demographic info"
        failMessage="Failed to update demographic info"
        open={isSuccessModalOpen}
        onClose={() => {
          setIsSuccessModalOpen(false);
          setIsError(false);
        }}
      />
    </>
  );
};

export default DemographicInfo;
